import React, { useState, useRef } from 'react';
import { 
  Modal, Box, Typography, Button, Checkbox, List, ListItem, 
  Collapse, IconButton, Avatar, Stack, Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VideoPlayer from '../components/ShareCoachingVideoPlayer';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from "packages/novous_web/src/assets/icons/Refresh.svg";

type SliderRefs = {
    [key: number]: HTMLDivElement | null;
  };
const videoURL = "https://d36bi0gkzuh8e7.cloudfront.net/ASCUS00055/warning/346430014652/2025-04-29/02_01_618_346430014652_250429_113440.mp4?Expires=1745993224&Signature=YIVvOMyIOTG9YHZDgLZzY0cRBtCzwMtJnfLcEcJQJyXk0fk2EDQMsrLXOe3MaWoyCLUgliM3L0nRnz52IUlUJcYXwI4MgmYcjXNuYKwqw4BB7UrVqZswDgo70IBu1FplVyS-ITJ7usCSPPFFYWv7ux5hR~kFbIcHtRzXKGWdGlDdmNayqmKq-nuQHoCBO67d8T9x7zOboRSlgO0NEr2vifcarlrKWbt2dV9ds~ENLeZ14JrDRO6vzac3JjCCQhQHt5fPQNod-oosLrA3hLya-Xgpr2lNCTfaIpdd90MemrA45ItLgBeVCTpfkzUxe2AHGEVPikoSnai2LFx2DbYuBg__&Key-Pair-Id=K27MJ4Q8SK0SG5";
const coachingPlaylists = [
  { 
    id: 1, 
    name: 'Safety Training', 
    videos: [
      { id: 1, url: videoURL, title: 'Safety Basics' },
      { id: 2, url: videoURL, title: 'Emergency Procedures' },
      { id: 3, url: videoURL, title: 'Defensive Driving' },
      { id: 4, url: videoURL, title: 'Night Driving' },
      { id: 5, url: videoURL, title: 'Weather Conditions' },
    ] 
  },
  { 
    id: 2, 
    name: 'Alert Response', 
    videos: [
      { id: 1, url: videoURL, title: 'SOS Response' },
      { id: 2, url: videoURL, title: 'Drowsiness Prevention' }
    ] 
  },
  { 
    id: 3, 
    name: 'Performance Tips', 
    videos: [
      { id: 1, url: videoURL, title: 'Fuel Efficiency' },
      { id: 2, url: videoURL, title: 'Route Optimization' },
      { id: 3, url: videoURL, title: 'Vehicle Maintenance' }
    ] 
  },
];

const suggestedPlaylist = {
  id: 0,
  name: 'Recommended Training',
  videos: [
    { id: 1, url: videoURL, title: 'Essential Safety' },
    { id: 2, url: videoURL, title: 'Alert Handling' }
  ]
};

const topAlerts = [
  { id: 1, name: 'SOS', count: 20, color: '#EEEDFC', icon: '🆘' },
  { id: 2, name: 'Drowsiness', count: 24, color: '#EEEDFC', icon: '😴' },
  { id: 3, name: 'Distracted', count: 7, color: '#EEEDFC', icon: '📵' },
];

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1400,
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const videoCardStyle = {
  width: '100%',
  height: 0,
  paddingBottom: '56.25%',
  position: 'relative' as const,
  overflow: 'hidden',
  borderRadius: 4,
  backgroundColor: '#000',
};

const playlistItemStyle = {
  border: '1px solid #e0e0e0',
  borderRadius: 2,
  mb: 2,
  overflow: 'hidden'
};

interface CoachingModalProps {
  open: boolean;
  onClose: () => void;
}

const CoachingModal: React.FC<CoachingModalProps> = ({ open, onClose }) => {
  const [expandedPlaylists, setExpandedPlaylists] = useState<number[]>([]);
  const [selectedPlaylists, setSelectedPlaylists] = useState<number[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<{ playlistId: number; videoId: number }[]>([]);
  const [currentIndices, setCurrentIndices] = useState<{[key: number]: number}>({});
  const [playingVideo, setPlayingVideo] = useState<{ playlistId: number; videoId: number } | null>(null);
  const sliderRefs = useRef<SliderRefs>({});

  const toggleExpand = (id: number) => {
    setExpandedPlaylists(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
    if (!currentIndices[id]) {
      setCurrentIndices(prev => ({ ...prev, [id]: 0 }));
    }
  };

  const toggleSelect = (id: number) => {
    const playlist = [...coachingPlaylists, suggestedPlaylist].find(p => p.id === id);
    if (!playlist) return;

    setSelectedPlaylists(prev => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        setSelectedVideos(prevVids => prevVids.filter(v => v.playlistId !== id));
        return prev.filter(item => item !== id);
      } else {
        const newVideos = playlist.videos.map(video => ({ playlistId: id, videoId: video.id }));
        setSelectedVideos(prevVids => [
          ...prevVids.filter(v => v.playlistId !== id),
          ...newVideos
        ]);
        return [...prev, id];
      }
    });
  };

  const handleVideoSelect = (playlistId: number, videoId: number) => {
    setSelectedVideos(prev => {
      const exists = prev.some(v => v.playlistId === playlistId && v.videoId === videoId);
      return exists 
        ? prev.filter(v => !(v.playlistId === playlistId && v.videoId === videoId))
        : [...prev, { playlistId, videoId }];
    });
  };

  const handleNext = (playlistId: number) => {
    const playlist = [...coachingPlaylists, suggestedPlaylist].find(p => p.id === playlistId);
    if (!playlist) return;
    
    setCurrentIndices(prev => {
      const current = prev[playlistId] || 0;
      const nextIndex = (current + 1) % playlist.videos.length;
      const slider = sliderRefs.current[playlistId];
      if (slider) {
        const videoWidth = slider.clientWidth / 3;
        slider.scrollTo({
          left: nextIndex * videoWidth,
          behavior: 'smooth'
        });
      }
      return { ...prev, [playlistId]: nextIndex };
    });
  };

  const handlePrev = (playlistId: number) => {
    const playlist = [...coachingPlaylists, suggestedPlaylist].find(p => p.id === playlistId);
    if (!playlist) return;

    setCurrentIndices(prev => {
      const current = prev[playlistId] || 0;
      const prevIndex = (current - 1 + playlist.videos.length) % playlist.videos.length;
      const slider = sliderRefs.current[playlistId];
      if (slider) {
        const videoWidth = slider.clientWidth / 3;
        slider.scrollTo({
          left: prevIndex * videoWidth,
          behavior: 'smooth'
        });
      }
      return { ...prev, [playlistId]: prevIndex };
    });
  };

  const handleVideoPlayPause = (playlistId: number, videoId: number) => {
    setPlayingVideo(prev => {
      if (prev?.playlistId === playlistId && prev?.videoId === videoId) {
        return null;
      }
      return { playlistId, videoId };
    });
  };

  const getVisibleVideos = (playlistId: number) => {
    const playlist = [...coachingPlaylists, suggestedPlaylist].find(p => p.id === playlistId);
    return playlist ? playlist.videos : [];
  };

  const handleSend = () => {
    const payload = [
      ...selectedPlaylists.flatMap(playlistId => {
        const playlist = [...coachingPlaylists, suggestedPlaylist].find(p => p.id === playlistId);
        return playlist ? playlist.videos.map(v => ({ playlistId, videoId: v.id })) : [];
      }),
      ...selectedVideos
    ].filter((v, i, a) => 
      a.findIndex(t => t.playlistId === v.playlistId && t.videoId === v.videoId) === i
    );
    
    console.log('Payload:', payload);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Share Coaching</Typography>
          <CustomIconButton
            text="Clear All"
            icon={<img src={refresh} alt="reset" />}
            background="transparent"
            variant="outlined"
            onClick={() => {
              setSelectedPlaylists([]);
              setSelectedVideos([]);
              setCurrentIndices({});
            }}
          />
        </Box>

        <Typography sx={{
          fontFamily: 'Inter Tight',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
          color: '#767676',
          mb: 3
        }}>
          Help drivers improve their performance by sharing targeted coaching content.
        </Typography>

        <Typography sx={{
          fontFamily: 'Inter Tight',
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '27px',
          mb: 2
        }}>
          Top 3 Alerts
        </Typography>
        
        <Stack direction="row" spacing={2} mb={4}>
          {topAlerts.map(alert => (
            <Box key={alert.id} sx={{
              width: 220,
              height: 48,
              borderRadius: '12px',
              padding: '12px 16px',
              background: alert.color,
              display: 'flex',
              alignItems: 'center'
            }}>
              <Avatar sx={{ bgcolor: 'transparent', width: 24, height: 24, mr: 1 }}>
                {alert.icon}
              </Avatar>
              <Typography sx={{ flexGrow: 1 }}>{alert.name}</Typography>
              <Typography sx={{ fontWeight: 'bold' }}>{alert.count}</Typography>
            </Box>
          ))}
        </Stack>

        <Typography variant="subtitle1" mb={1}>Suggested Playlist</Typography>
        <Paper elevation={0} sx={playlistItemStyle}>
          <ListItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              cursor: 'pointer',
              bgcolor: expandedPlaylists.includes(suggestedPlaylist.id) ? '#f5f5f5' : 'inherit'
            }}
            onClick={() => toggleExpand(suggestedPlaylist.id)}
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={selectedPlaylists.includes(suggestedPlaylist.id)}
                onChange={() => toggleSelect(suggestedPlaylist.id)}
                onClick={e => e.stopPropagation()}
              />
              <Typography>{suggestedPlaylist.name}</Typography>
            </Box>
            <IconButton onClick={e => { e.stopPropagation(); toggleExpand(suggestedPlaylist.id); }}>
              {expandedPlaylists.includes(suggestedPlaylist.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </ListItem>

          <Collapse in={expandedPlaylists.includes(suggestedPlaylist.id)} timeout="auto" unmountOnExit>
            <Box p={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => handlePrev(suggestedPlaylist.id)}>
                  <ArrowBackIosIcon />
                </IconButton>

                <Box 
                ref={(el: HTMLDivElement) => {
                    sliderRefs.current[suggestedPlaylist.id] = el;
                  }}
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    overflowX: 'hidden',
                    scrollBehavior: 'smooth',
                    gap: 2,
                    width: '100%'
                  }}
                >
                  {getVisibleVideos(suggestedPlaylist.id).map(video => (
                    <Box key={video.id} sx={{ flex: '0 0 30%', minWidth: '30%' }}>
                      <Box sx={videoCardStyle}>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                          <Checkbox
                            checked={selectedVideos.some(v => 
                              v.playlistId === suggestedPlaylist.id && v.videoId === video.id
                            )}
                            onChange={() => handleVideoSelect(suggestedPlaylist.id, video.id)}
                            sx={{ 
                              position: 'absolute', 
                              left: 8, 
                              top: 8, 
                              zIndex: 1, 
                              color: 'white',
                              '&.Mui-checked': { color: 'white' }
                            }}
                          />
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 1,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                            }}
                            onClick={() => handleVideoPlayPause(suggestedPlaylist.id, video.id)}
                          >
                            {playingVideo?.playlistId === suggestedPlaylist.id && 
                            playingVideo?.videoId === video.id ? (
                              <PauseIcon sx={{ color: 'white', fontSize: 40 }} />
                            ) : (
                              <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                            )}
                          </IconButton>
                          <VideoPlayer
                            key={`${suggestedPlaylist.id}-${video.id}`}
                            streamUrl={video.url}
                            availableHeight={200}
                            availableWidth={350}
                            isPlaying={playingVideo?.playlistId === suggestedPlaylist.id && 
                                      playingVideo?.videoId === video.id}
                            onPlayPause={(playing: any) => {
                              if (!playing) setPlayingVideo(null);
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="subtitle2" mt={1} textAlign="center">
                        {video.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <IconButton onClick={() => handleNext(suggestedPlaylist.id)}>
                  <ArrowForwardIosIcon />
                </IconButton>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        <Typography variant="subtitle1" mb={1}>All Playlists</Typography>
        <List sx={{ p: 0 }}>
          {coachingPlaylists.map(playlist => (
            <Paper key={playlist.id} elevation={0} sx={playlistItemStyle}>
              <ListItem
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  bgcolor: expandedPlaylists.includes(playlist.id) ? '#f5f5f5' : 'inherit'
                }}
                onClick={() => toggleExpand(playlist.id)}
              >
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={selectedPlaylists.includes(playlist.id)}
                    onChange={() => toggleSelect(playlist.id)}
                    onClick={e => e.stopPropagation()}
                  />
                  <Typography>{playlist.name}</Typography>
                </Box>
                <IconButton onClick={e => { e.stopPropagation(); toggleExpand(playlist.id); }}>
                  {expandedPlaylists.includes(playlist.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </ListItem>

              <Collapse in={expandedPlaylists.includes(playlist.id)} timeout="auto" unmountOnExit>
                <Box p={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => handlePrev(playlist.id)}>
                      <ArrowBackIosIcon />
                    </IconButton>

                    <Box 
                       ref={(el: HTMLDivElement) => {
                        sliderRefs.current[playlist.id] = el;
                      }}
                      sx={{
                        display: 'flex',
                        flexGrow: 1,
                        overflowX: 'hidden',
                        scrollBehavior: 'smooth',
                        gap: 2,
                        width: '100%'
                      }}
                    >
                      {getVisibleVideos(playlist.id).map(video => (
                        <Box key={video.id} sx={{ flex: '0 0 30%', minWidth: '30%' }}>
                          <Box sx={videoCardStyle}>
                            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                              <Checkbox
                                checked={selectedVideos.some(v => 
                                  v.playlistId === playlist.id && v.videoId === video.id
                                )}
                                onChange={() => handleVideoSelect(playlist.id, video.id)}
                                sx={{ 
                                  position: 'absolute', 
                                  left: 8, 
                                  top: 8, 
                                  zIndex: 1, 
                                  color: 'white',
                                  '&.Mui-checked': { color: 'white' }
                                }}
                              />
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 1,
                                  backgroundColor: 'rgba(0,0,0,0.6)',
                                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                }}
                                onClick={() => handleVideoPlayPause(playlist.id, video.id)}
                              >
                                {playingVideo?.playlistId === playlist.id && 
                                playingVideo?.videoId === video.id ? (
                                  <PauseIcon sx={{ color: 'white', fontSize: 40 }} />
                                ) : (
                                  <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                                )}
                              </IconButton>
                              <VideoPlayer
                                key={`${playlist.id}-${video.id}`}
                                streamUrl={video.url}
                                availableHeight={200}
                                availableWidth={350}
                                isPlaying={playingVideo?.playlistId === playlist.id && 
                                          playingVideo?.videoId === video.id}
                                onPlayPause={(playing: any) => {
                                  if (!playing) setPlayingVideo(null);
                                }}
                              />
                            </Box>
                          </Box>
                          <Typography variant="subtitle2" mt={1} textAlign="center">
                            {video.title}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <IconButton onClick={() => handleNext(playlist.id)}>
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </List>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSend}>
            Send
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CoachingModal;